<template>
  <div class="rentability-chart" data-testid="rentability-chart">
    <section class="rentability-chart__section">
      <div class="rentability-chart__filters" />
      <RentabilityHeader
        :is-loading="isHeaderLoading"
        :has-error="hasError"
        :portfolio-chart-summary="portfolioChartSummary"
        :period-filters="periodFilters"
        :benchmark-filters="benchmarkFilters"
        @on-benchmark-filter-change="handleBenchmarkFilterChange"
        @on-period-filter-change="handlePeriodFilterChange"
      >
        <template #filter-options>
          <slot name="filter-options" />
        </template>
      </RentabilityHeader>
      <PerformanceChart
        :axis-x="chartAxisX"
        :axis-y="chartAxisY"
        :benchmark-filters="benchmarkFilters"
        :updated-at="updatedAt"
        :is-loading="isChartLoading"
        :has-error="hasError"
        @on-animation-state-change="handleOnAnimationStateChange"
        @on-retry="getPortfolioChartData"
      />
      <PerformanceLink :portfolio-id="portfolioId" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';

import { RentabilityHeader } from './components/RentabilityHeader';
import { PerformanceChart } from '@/modules/wealth/components/PerformanceChart';
import { PerformanceLink } from './components/PerformanceLink';

import {
  getPortfolioChart,
  PortfolioChartAxisX,
  PortfolioChartAxisY,
  PortfolioChartFilter,
  PortfolioChartParams,
  PortfolioChartSummary
} from '@/modules/wealth/services/portfolio';
import { getFilterOptions } from '@/modules/wealth/views/IndexPortfolio/helpers';

interface Props {
  portfolioId: string;
  isLoadingPortfolio?: boolean;
  hasErrorPortfolio?: boolean;
}
const props = defineProps<Props>();

const isLoading = ref(false);
const hasError = ref(false);
const periodFilters = ref<PortfolioChartFilter[]>([]);
const benchmarkFilters = ref<PortfolioChartFilter[]>([]);
const portfolioChartSummary = ref<PortfolioChartSummary | null>(null);
const chartAxisX = ref<PortfolioChartAxisX[]>([]);
const chartAxisY = ref<PortfolioChartAxisY[]>([]);
const updatedAt = ref<string | null>(null);
const isChartAnimationInProgress = ref(false);

const portfolioParams = computed<PortfolioChartParams>(() => {
  return {
    portfolioId: props.portfolioId,
    periodFilter: getActiveFilter(periodFilters.value),
    benchmark: getActiveFilter(benchmarkFilters.value)
  };
});

const isChartLoading = computed(() => {
  return (
    isLoading.value &&
    chartAxisX.value.length === 0 &&
    chartAxisY.value.length === 0
  );
});

const isHeaderLoading = computed(() => {
  return isLoading.value || isChartAnimationInProgress.value;
});

const getPortfolioChartData = async () => {
  isLoading.value = true;
  hasError.value = false;

  try {
    const portfolioChart = await getPortfolioChart(portfolioParams.value);

    periodFilters.value = getFilterOptions(portfolioChart.periodFilters);
    benchmarkFilters.value = getFilterOptions(portfolioChart.benchmarks);
    portfolioChartSummary.value = portfolioChart.summary;
    chartAxisX.value = portfolioChart.axisX;
    chartAxisY.value = portfolioChart.axisY;
    updatedAt.value = portfolioChart.updatedAt.formatted;
  } catch (error) {
    hasError.value = true;
    isChartAnimationInProgress.value = false;
  } finally {
    isLoading.value = false;
  }
};

const handlePeriodFilterChange = async (value: string) => {
  periodFilters.value = periodFilters.value.map(filter => ({
    ...filter,
    active: filter.value === value
  }));
  getPortfolioChartData();
};

const handleBenchmarkFilterChange = async (value: string) => {
  benchmarkFilters.value = benchmarkFilters.value.map(filter => ({
    ...filter,
    active: filter.value === value
  }));
  await getPortfolioChartData();
};

const handleOnAnimationStateChange = (isAnimationInProgress: boolean) => {
  isChartAnimationInProgress.value = isAnimationInProgress;
};

const getActiveFilter = (filters: PortfolioChartFilter[] | null) => {
  if (!filters) {
    return '';
  }

  return filters.find(filter => filter.active)?.key;
};

onMounted(getPortfolioChartData);
</script>

<style lang="less" scoped>
.rentability-chart {
  &__section-divider {
    width: 100%;
    height: @size-border-x400;
    background-color: @divider-primary;
    margin: @size-spacing-x1200 0;
  }
}
</style>
